/**
 * Envío AP-11 · «Capturar con cámara»: foto o video (5–30 s) desde la cámara
 * del dispositivo, con cuenta regresiva, revisión, recorte y destino en el
 * Canvas (Dream Actor por defecto para video). El video se recorta y pasa a
 * MP4 en el servidor (/files/camera-clip) en una sola pasada.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Camera, Video, X, Mic, Upload, RotateCcw, Check, Loader2, Info, CameraOff, Play, Pause, SwitchCamera,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  COUNTDOWN_SECONDS, DEFAULT_PHOTO_DEST, DEFAULT_VIDEO_DEST, DURATIONS, ERROR_TEXT, PHOTO_DESTINATIONS, QUALITY,
  VIDEO_DESTINATIONS, cameraErrorCase, captureName, clampTrim, destinationFor, extForMime, fmtClock, fmtSec,
  loadDuration, pickRecorderMime, saveDuration, trimProblem,
} from '../../utils/cameraCapture';
import { uploadCameraClip } from '../../services/cameraClipService';
import VideoTrimmer from './VideoTrimmer';
import '../../styles/camera.css';

const stopStream = (s) => { try { s?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* */ } };
const isTyping = (el) => !!el?.closest?.('input, select, textarea, [contenteditable="true"]');

export default function CameraCaptureModal({
  accept = 'both', targetLabel = '', linkKind = null, onPhoto, onVideo, onUpload, onClose,
}) {
  const [mode, setMode] = useState(accept === 'video' ? 'video' : 'photo');
  const [phase, setPhase] = useState('starting');       // starting|live|countdown|recording|review|saving|error
  const [errCase, setErrCase] = useState(null);
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [quality, setQuality] = useState('720p');
  const [audio, setAudio] = useState(false);
  const [countdownOn, setCountdownOn] = useState(true);
  const [maxSec, setMaxSec] = useState(() => loadDuration());
  const [count, setCount] = useState(COUNTDOWN_SECONDS);
  const [elapsed, setElapsed] = useState(0);
  const [clip, setClip] = useState(null);                 // {blob,url,mime,duration}
  const [photo, setPhoto] = useState(null);               // {blob,url,w,h}
  const [trim, setTrim] = useState({ start: 0, end: 0 });
  const [playhead, setPlayhead] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [videoDest, setVideoDest] = useState(linkKind === 'video' ? DEFAULT_VIDEO_DEST : 'none');
  const [photoDest, setPhotoDest] = useState(DEFAULT_PHOTO_DEST);
  const [progress, setProgress] = useState(null);
  const [notice, setNotice] = useState('');

  const liveRef = useRef(null);
  const reviewRef = useRef(null);
  const streamRef = useRef(null);
  const audioStreamRef = useRef(null);
  const recRef = useRef(null);
  const timersRef = useRef([]);
  const t0Ref = useRef(0);
  const discardRef = useRef(false);
  const mountedRef = useRef(true);

  const isVideo = mode === 'video';
  const showLink = (isVideo && linkKind === 'video') || (!isVideo && linkKind === 'image');
  const dest = isVideo ? destinationFor('video', videoDest) : destinationFor('photo', photoDest);
  const q = QUALITY[quality];

  const clearTimers = () => { timersRef.current.forEach((t) => { clearTimeout(t); clearInterval(t); }); timersRef.current = []; };

  const startStream = useCallback(async (id = deviceId) => {
    setPhase('starting');
    setErrCase(null);
    stopStream(streamRef.current);
    streamRef.current = null;
    if (!navigator.mediaDevices?.getUserMedia) {
      setErrCase(cameraErrorCase({ name: 'Unsupported' })); setPhase('error'); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          ...(id ? { deviceId: { exact: id } } : { facingMode: 'user' }),
          width: { ideal: q.width }, height: { ideal: q.height },
        },
        audio: false,
      });
      if (!mountedRef.current) { stopStream(stream); return; }
      streamRef.current = stream;
      if (liveRef.current) { liveRef.current.srcObject = stream; liveRef.current.play?.().catch(() => {}); }
      try {
        const list = (await navigator.mediaDevices.enumerateDevices()).filter((d) => d.kind === 'videoinput');
        setDevices(list);
        const cur = stream.getVideoTracks()[0]?.getSettings?.().deviceId;
        if (cur) setDeviceId(cur);
      } catch (_e) { /* sin lista */ }
      setPhase('live');
    } catch (err) {
      setErrCase(cameraErrorCase(err));
      setPhase('error');
    }
  }, [deviceId, q.width, q.height]);

  // abrir / cerrar
  useEffect(() => {
    mountedRef.current = true;
    startStream('');
    return () => {
      mountedRef.current = false;
      clearTimers();
      discardRef.current = true;
      try { if (recRef.current?.state === 'recording') recRef.current.stop(); } catch (_e) { /* */ }
      stopStream(streamRef.current);
      stopStream(audioStreamRef.current);
    };
    // eslint-disable-next-line
  }, []);

  // cambiar de cámara o calidad
  useEffect(() => {
    if (phase === 'live') startStream(deviceId);
    // eslint-disable-next-line
  }, [quality]);

  // re-enganchar la vista en vivo al volver de «revisar»
  useEffect(() => {
    if ((phase === 'live' || phase === 'countdown' || phase === 'recording') && liveRef.current && streamRef.current
      && liveRef.current.srcObject !== streamRef.current) {
      liveRef.current.srcObject = streamRef.current;
      liveRef.current.play?.().catch(() => {});
    }
  }, [phase]);

  // liberar URLs locales
  useEffect(() => () => { if (clip?.url) URL.revokeObjectURL(clip.url); }, [clip]);
  useEffect(() => () => { if (photo?.url) URL.revokeObjectURL(photo.url); }, [photo]);

  // el destino puede achicar el tramo (p. ej. Video con sonido, 8,7 s)
  useEffect(() => {
    if (!clip || !isVideo) return;
    const next = clampTrim(trim.start, trim.end, clip.duration, dest);
    if (next.end !== trim.end || next.start !== trim.start) {
      setTrim(next);
      setNotice(`Se ajustó el tramo a ${fmtSec(next.end - next.start)}: ${dest.label} acepta hasta ${fmtSec(dest.max)}.`);
    }
    // eslint-disable-next-line
  }, [videoDest, clip]);

  const pickDuration = (v) => { setMaxSec(v); saveDuration(v); };

  const takePhoto = () => {
    const v = liveRef.current;
    if (!v?.videoWidth) return;
    const c = document.createElement('canvas');
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext('2d').drawImage(v, 0, 0);             // sin espejo: como te ven los demás
    c.toBlob((blob) => {
      if (!blob) return;
      setPhoto({ blob, url: URL.createObjectURL(blob), w: c.width, h: c.height });
      setPhase('review');
    }, 'image/jpeg', 0.92);
  };

  const beginRecording = async () => {
    let stream = streamRef.current;
    if (!stream) return;
    if (audio) {
      try {
        const a = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStreamRef.current = a;
        stream = new MediaStream([...stream.getVideoTracks(), ...a.getAudioTracks()]);
      } catch (_e) {
        toast.message('No se pudo usar el micrófono: se graba sin audio.', { position: 'bottom-right' });
      }
    }
    const mime = typeof MediaRecorder !== 'undefined' ? pickRecorderMime((m) => MediaRecorder.isTypeSupported(m)) : '';
    let rec;
    try {
      rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: q.bitrate });
    } catch (err) {
      setErrCase('unsupported'); setPhase('error'); return;
    }
    const chunks = [];
    discardRef.current = false;
    rec.ondataavailable = (e) => { if (e.data?.size) chunks.push(e.data); };
    rec.onstop = () => {
      clearTimers();
      stopStream(audioStreamRef.current); audioStreamRef.current = null;
      if (discardRef.current || !mountedRef.current) return;
      const duration = Math.min((performance.now() - t0Ref.current) / 1000, maxSec);
      const type = rec.mimeType || mime || 'video/webm';
      const blob = new Blob(chunks, { type });
      if (duration < 1 || !blob.size) {
        toast.error('La grabación fue demasiado corta. Probá de nuevo.', { position: 'bottom-right' });
        setPhase('live'); return;
      }
      setClip({ blob, url: URL.createObjectURL(blob), mime: type, duration: Math.round(duration * 100) / 100 });
      setTrim(clampTrim(0, duration, duration, destinationFor('video', videoDest)));
      setPlayhead(0); setPlaying(false); setNotice('');
      setPhase('review');
    };
    recRef.current = rec;
    rec.start(250);
    t0Ref.current = performance.now();
    setElapsed(0);
    setPhase('recording');
    timersRef.current.push(setInterval(() => setElapsed((performance.now() - t0Ref.current) / 1000), 100));
    timersRef.current.push(setTimeout(() => stopRecording(), maxSec * 1000));
  };

  const stopRecording = () => {
    try { if (recRef.current?.state === 'recording') recRef.current.stop(); } catch (_e) { /* */ }
  };

  const startVideo = () => {
    if (!countdownOn) { beginRecording(); return; }
    setCount(COUNTDOWN_SECONDS);
    setPhase('countdown');
    let n = COUNTDOWN_SECONDS;
    const iv = setInterval(() => {
      n -= 1;
      if (n <= 0) { clearTimers(); beginRecording(); } else setCount(n);
    }, 1000);
    timersRef.current.push(iv);
  };

  const cancelCapture = () => {
    clearTimers();
    if (phase === 'recording') { discardRef.current = true; stopRecording(); }
    setPhase('live');
  };

  const shutter = () => {
    if (phase === 'live') { if (isVideo) startVideo(); else takePhoto(); }
    else if (phase === 'recording') stopRecording();
    else if (phase === 'countdown') cancelCapture();
  };

  const retake = () => {
    setClip(null); setPhoto(null); setNotice('');
    setPhase('live');
  };

  const usePhoto = () => {
    if (!photo) return;
    const file = new File([photo.blob], `${captureName('photo')}.jpg`, { type: 'image/jpeg' });
    onPhoto?.(file, showLink ? photoDest : 'none');
  };

  const useVideo = async () => {
    if (!clip) return;
    const problem = trimProblem(trim.start, trim.end, showLink ? dest : null);
    if (problem) { toast.error(problem, { position: 'bottom-right' }); return; }
    setPhase('saving'); setProgress(0);
    try {
      const out = await uploadCameraClip(clip.blob, {
        start: trim.start, end: trim.end, audio,
        filename: `${captureName('video')}${extForMime(clip.mime)}`, onProgress: setProgress,
      });
      if (!mountedRef.current) return;
      onVideo?.(out, showLink ? videoDest : 'none');
    } catch (err) {
      if (!mountedRef.current) return;
      toast.error(err?.userMessage || 'No se pudo guardar el video. Probá de nuevo.', { position: 'bottom-right', duration: 8000 });
      setPhase('review');
    } finally {
      if (mountedRef.current) setProgress(null);
    }
  };

  const close = () => { if (phase !== 'saving') onClose?.(); };

  // teclado: espacio = disparar/detener, Esc = cancelar o cerrar
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation(); e.preventDefault();
        if (phase === 'recording' || phase === 'countdown') cancelCapture(); else close();
      } else if ((e.key === ' ' || e.code === 'Space') && !isTyping(e.target)
        && (phase === 'live' || phase === 'recording' || phase === 'countdown')) {
        e.preventDefault(); e.stopPropagation();
        shutter();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  // revisión: reproducir sólo el tramo
  const onReviewTime = () => {
    const v = reviewRef.current;
    if (!v) return;
    if (v.currentTime >= trim.end - 0.03 || v.currentTime < trim.start - 0.05) v.currentTime = trim.start;
    setPlayhead(v.currentTime);
  };
  const togglePlay = () => {
    const v = reviewRef.current;
    if (!v) return;
    if (v.paused) {
      if (v.currentTime < trim.start || v.currentTime >= trim.end) v.currentTime = trim.start;
      v.play().catch(() => {}); setPlaying(true);
    } else { v.pause(); setPlaying(false); }
  };
  const seek = (t) => {
    const v = reviewRef.current;
    if (v) { v.currentTime = Math.min(Math.max(t, trim.start), trim.end); setPlayhead(v.currentTime); }
  };
  const onTrim = (next) => {
    setTrim(next); setNotice('');
    const v = reviewRef.current;
    if (v && (v.currentTime < next.start || v.currentTime > next.end)) { v.currentTime = next.start; setPlayhead(next.start); }
  };

  const busy = phase === 'countdown' || phase === 'recording';
  const left = Math.max(0, maxSec - elapsed);
  const destList = isVideo ? VIDEO_DESTINATIONS : PHOTO_DESTINATIONS;
  const trimLen = trim.end - trim.start;
  const problem = clip && isVideo ? trimProblem(trim.start, trim.end, showLink ? dest : null) : null;
  const errText = ERROR_TEXT[errCase] || ERROR_TEXT.other;

  const sideTip = useMemo(() => (isVideo
    ? ['Referencia de movimiento', 'Fondo liso, buena luz y el cuerpo entero en cuadro. 5 a 10 s alcanzan para que el modelo copie el gesto.']
    : ['Foto para el Canvas', 'Luz de frente y fondo simple. Después la podés recortar o retocar con Editar imagen.']), [isVideo]);

  const content = (
    <div className="cam-overlay nodrag nowheel" onPointerDown={(e) => e.stopPropagation()} data-testid="cam-modal">
      <section className="cam-modal" role="dialog" aria-modal="true" aria-label="Capturar con cámara">
        <header className="cam-head">
          {isVideo ? <Video size={20} className="cam-accent" aria-hidden="true" /> : <Camera size={20} className="cam-accent" aria-hidden="true" />}
          <div className="cam-head-txt">
            <span className="cam-kicker">{phase === 'review' || phase === 'saving' ? 'REVISAR' : 'CAPTURAR CON CÁMARA'}</span>
            {targetLabel && <span className="cam-target">Para: {targetLabel}</span>}
          </div>
          <button type="button" className="cam-icon-btn" onClick={close} disabled={busy || phase === 'saving'} aria-label="Cerrar" data-testid="cam-close"><X size={18} /></button>
        </header>

        {phase === 'error' ? (
          <div className="cam-error" data-testid="cam-error" data-case={errCase}>
            <div className="cam-error-ico"><CameraOff size={30} aria-hidden="true" /></div>
            <h2>{errText[0]}</h2>
            <p>{errText[1]}</p>
            {errCase === 'denied' && (
              <ol>
                <li>Tocá el candado a la izquierda de la dirección.</li>
                <li>En «Cámara», elegí <b>Permitir</b>.</li>
                <li>Volvé acá y tocá «Probar de nuevo».</li>
              </ol>
            )}
            <div className="cam-row">
              {onUpload && <button type="button" className="cam-btn ghost" onClick={onUpload}><Upload size={15} aria-hidden="true" /> Subir un archivo</button>}
              <button type="button" className="cam-btn primary" onClick={() => startStream(deviceId)} data-testid="cam-retry"><RotateCcw size={15} aria-hidden="true" /> Probar de nuevo</button>
            </div>
          </div>
        ) : (
          <div className="cam-body">
            <div className="cam-main">
              {(phase === 'review' || phase === 'saving') ? (
                <>
                  <div className="cam-stage">
                    {isVideo && clip ? (
                      <>
                        {/* eslint-disable-next-line */}
                        <video ref={reviewRef} src={clip.url} className="cam-video" playsInline
                          onTimeUpdate={onReviewTime} onLoadedMetadata={() => seek(trim.start)}
                          onPause={() => setPlaying(false)} data-testid="cam-review-video" />
                        <button type="button" className="cam-play" onClick={togglePlay} aria-label={playing ? 'Pausar' : 'Reproducir'}>
                          {playing ? <Pause size={24} aria-hidden="true" /> : <Play size={24} aria-hidden="true" />}
                        </button>
                      </>
                    ) : photo ? <img src={photo.url} alt="Foto capturada" className="cam-video" data-testid="cam-review-photo" /> : null}
                    <span className="cam-pill tl">Así se va a guardar (sin espejo)</span>
                  </div>
                  {isVideo && clip && (
                    <VideoTrimmer total={clip.duration} start={trim.start} end={trim.end} playhead={playhead}
                      dest={showLink ? dest : null} onChange={onTrim} onSeek={seek} />
                  )}
                  {notice && <p className="cam-notice" role="status">{notice}</p>}
                </>
              ) : (
                <>
                  <div className={`cam-stage ${phase === 'recording' ? 'is-rec' : ''}`}>
                    {/* eslint-disable-next-line */}
                    <video ref={liveRef} className="cam-video is-mirror" autoPlay muted playsInline data-testid="cam-live" />
                    <div className="cam-grid" aria-hidden="true" />
                    {phase === 'starting' && <div className="cam-center"><Loader2 size={26} className="cam-spin" aria-hidden="true" /><span>Abriendo la cámara…</span></div>}
                    {phase === 'countdown' && (
                      <div className="cam-center is-dim" data-testid="cam-countdown">
                        <span className="cam-count">{count}</span>
                        <span>Acomodate… empieza a grabar en {count} s</span>
                      </div>
                    )}
                    {phase === 'recording' ? (
                      <>
                        <span className="cam-pill tl is-rec" data-testid="cam-rec"><span className="cam-dot" />REC {fmtClock(elapsed)}</span>
                        <span className="cam-pill tr">Quedan {Math.ceil(left)} s</span>
                        <div className="cam-progress"><div style={{ width: `${Math.min(100, (elapsed / maxSec) * 100)}%` }} /></div>
                      </>
                    ) : (
                      <>
                        <span className="cam-pill tl">Vista en vivo · espejo</span>
                        {isVideo && <span className="cam-pill tr">00:00 / {fmtClock(maxSec)}</span>}
                        {phase === 'live' && <span className="cam-pill bc">{isVideo ? 'Encuadrá el cuerpo entero para un movimiento' : 'Centrá la cara o el objeto'}</span>}
                      </>
                    )}
                  </div>
                  <div className="cam-controls">
                    {accept === 'both' ? (
                      <div className="cam-seg" role="group" aria-label="Modo">
                        <button type="button" aria-pressed={!isVideo} className={!isVideo ? 'is-on' : ''} disabled={busy}
                          onClick={() => setMode('photo')} data-testid="cam-mode-photo"><Camera size={16} aria-hidden="true" /> Foto</button>
                        <button type="button" aria-pressed={isVideo} className={isVideo ? 'is-on' : ''} disabled={busy}
                          onClick={() => setMode('video')} data-testid="cam-mode-video"><Video size={16} aria-hidden="true" /> Video</button>
                      </div>
                    ) : <span className="cam-mode-fixed">{isVideo ? <><Video size={16} aria-hidden="true" /> Video</> : <><Camera size={16} aria-hidden="true" /> Foto</>}</span>}
                    <div className="cam-shutter-wrap">
                      <button type="button" className="cam-shutter" onClick={shutter} disabled={phase === 'starting'}
                        aria-label={phase === 'recording' ? 'Detener grabación' : phase === 'countdown' ? 'Cancelar cuenta regresiva' : isVideo ? 'Empezar a grabar' : 'Sacar foto'}
                        data-testid="cam-shutter">
                        <span className={`cam-shutter-in ${isVideo ? 'is-video' : ''} ${phase === 'recording' ? 'is-stop' : ''}`} />
                      </button>
                    </div>
                    <span className="cam-hint">{phase === 'recording' ? 'Espacio para detener' : isVideo ? `Hasta ${maxSec} s · espacio para grabar` : 'Espacio para sacar la foto'}</span>
                  </div>
                </>
              )}
            </div>

            <aside className="cam-side" aria-label={phase === 'review' ? 'Detalle y destino' : 'Opciones de captura'}>
              {(phase === 'review' || phase === 'saving') ? (
                <>
                  <div className="cam-field">
                    <span className="cam-label">Captura</span>
                    <div className="cam-stats">
                      {isVideo && clip ? (
                        <>
                          <div><small>Duración</small><b data-testid="cam-final-len">{fmtSec(trimLen)}</b></div>
                          <div><small>Grabado</small><b>{fmtSec(clip.duration)}</b></div>
                          <div><small>Formato</small><b>MP4</b></div>
                          <div><small>Audio</small><b>{audio ? 'Sí' : 'No'}</b></div>
                        </>
                      ) : photo ? (
                        <>
                          <div><small>Tamaño</small><b>{photo.w}×{photo.h}</b></div>
                          <div><small>Formato</small><b>JPG</b></div>
                        </>
                      ) : null}
                    </div>
                  </div>
                  {showLink && (
                    <fieldset className="cam-dests" disabled={phase === 'saving'}>
                      <legend className="cam-label">Conectar a</legend>
                      {destList.map((d) => {
                        const on = (isVideo ? videoDest : photoDest) === d.key;
                        return (
                          <label key={d.key} className={`cam-dest ${on ? 'is-on' : ''}`}>
                            <input type="radio" name="cam-dest" checked={on}
                              onChange={() => (isVideo ? setVideoDest(d.key) : setPhotoDest(d.key))} data-testid={`cam-dest-${d.key}`} />
                            <span className="cam-dest-name">{d.label}</span>
                            {isVideo && d.type && <span className="cam-dest-lim">{d.max ? (d.min > 1 ? `${d.min}–${String(d.max).replace('.', ',')} s` : `hasta ${String(d.max).replace('.', ',')} s`) : 'sin tope'}</span>}
                          </label>
                        );
                      })}
                      <span className="cam-small">{dest?.type ? `Se crea ${dest.label} al lado, ya conectado.` : 'Queda cargado en este nodo.'} Siempre se guarda en la Galería.</span>
                    </fieldset>
                  )}
                  {problem && <p className="cam-problem" role="alert" data-testid="cam-problem">{problem}</p>}
                  <div className="cam-actions">
                    <button type="button" className="cam-btn primary" onClick={isVideo ? useVideo : usePhoto}
                      disabled={phase === 'saving' || !!problem} data-testid="cam-use">
                      {phase === 'saving' ? <Loader2 size={16} className="cam-spin" aria-hidden="true" /> : <Check size={16} aria-hidden="true" />}
                      {phase === 'saving' ? (progress != null && progress < 100 ? `Subiendo ${progress}%` : 'Recortando y guardando…') : isVideo ? 'Usar video' : 'Usar foto'}
                    </button>
                    <button type="button" className="cam-btn ghost" onClick={retake} disabled={phase === 'saving'} data-testid="cam-retake">
                      <RotateCcw size={15} aria-hidden="true" /> Repetir
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {devices.length > 1 && (
                    <label className="cam-field">
                      <span className="cam-label">Cámara</span>
                      <span className="cam-row">
                        <select value={deviceId} disabled={busy} onChange={(e) => { setDeviceId(e.target.value); startStream(e.target.value); }} data-testid="cam-device">
                          {devices.map((d, i) => <option key={d.deviceId || i} value={d.deviceId}>{d.label || `Cámara ${i + 1}`}</option>)}
                        </select>
                        <button type="button" className="cam-icon-btn" aria-label="Cambiar de cámara" disabled={busy}
                          onClick={() => {
                            const i = devices.findIndex((d) => d.deviceId === deviceId);
                            const next = devices[(i + 1) % devices.length]?.deviceId;
                            if (next) { setDeviceId(next); startStream(next); }
                          }}><SwitchCamera size={17} /></button>
                      </span>
                    </label>
                  )}
                  <div className="cam-field">
                    <span className="cam-label">Calidad</span>
                    <div className="cam-grid2" role="group" aria-label="Calidad">
                      {Object.keys(QUALITY).map((k) => (
                        <button key={k} type="button" aria-pressed={quality === k} className={`cam-chip ${quality === k ? 'is-on' : ''}`}
                          disabled={busy} onClick={() => setQuality(k)}>{k}</button>
                      ))}
                    </div>
                  </div>
                  {isVideo && (
                    <>
                      <div className="cam-field">
                        <span className="cam-label">Duración máxima</span>
                        <div className="cam-grid5" role="group" aria-label="Duración máxima">
                          {DURATIONS.map((s) => (
                            <button key={s} type="button" aria-pressed={maxSec === s} className={`cam-chip ${maxSec === s ? 'is-on' : ''}`}
                              disabled={busy} onClick={() => pickDuration(s)} data-testid={`cam-dur-${s}`}>{s} s</button>
                          ))}
                        </div>
                        <span className="cam-small">Corta sola al llegar. Podés detener antes.</span>
                      </div>
                      <label className="cam-toggle">
                        <Mic size={16} aria-hidden="true" />
                        <span>Grabar audio</span>
                        <input type="checkbox" checked={audio} disabled={busy} onChange={(e) => setAudio(e.target.checked)} data-testid="cam-audio" />
                      </label>
                      <label className="cam-toggle">
                        <span>Cuenta regresiva {COUNTDOWN_SECONDS} s</span>
                        <input type="checkbox" checked={countdownOn} disabled={busy} onChange={(e) => setCountdownOn(e.target.checked)} data-testid="cam-countdown-on" />
                      </label>
                    </>
                  )}
                  <div className="cam-tip"><b>{sideTip[0]}</b><span>{sideTip[1]}</span></div>
                  <div className="cam-foot-note">
                    <Info size={16} aria-hidden="true" />
                    <span>Se guarda en tu <b>Galería</b> y queda cargado en el nodo. Sin espejo: como te ven los demás.</span>
                  </div>
                </>
              )}
            </aside>
          </div>
        )}
      </section>
    </div>
  );

  return createPortal(content, document.body);
}
